/**
 * Generic object pool to avoid allocation during gameplay.
 * Works with any class that has activate() / deactivate() methods.
 */
export class ObjectPool {
    /** @type {Function} Factory that creates a new instance */
    _factory = null;

    /** @type {Object[]} Available (inactive) objects */
    _pool = [];

    /** @type {Object[]} Currently active objects */
    _active = [];

    /**
     * @param {Function} factory — () => newInstance. Must return an object
     *   with at minimum: active (bool), display (Container).
     * @param {number} [preAllocate=0] — number of objects to create upfront
     */
    constructor(factory, preAllocate = 0) {
        this._factory = factory;
        for (let i = 0; i < preAllocate; i++) {
            this._pool.push(this._factory());
        }
    }

    /**
     * Get an object from the pool (or create a new one).
     * @returns {Object}
     */
    get() {
        const obj = this._pool.length > 0 ? this._pool.pop() : this._factory();
        obj.active = true;
        obj.display.visible = true;
        this._active.push(obj);
        return obj;
    }

    /**
     * Return an object to the pool.
     * @param {Object} obj
     */
    release(obj) {
        obj.active = false;
        obj.display.visible = false;
        const idx = this._active.indexOf(obj);
        if (idx !== -1) this._active.splice(idx, 1);
        this._pool.push(obj);
    }

    /** Release all active objects back to the pool */
    releaseAll() {
        while (this._active.length > 0) {
            this.release(this._active[this._active.length - 1]);
        }
    }

    /** @returns {Object[]} Currently active objects (read-only reference) */
    get activeObjects() {
        return this._active;
    }

    /** @returns {number} */
    get activeCount() {
        return this._active.length;
    }

    /** @returns {number} */
    get poolSize() {
        return this._pool.length;
    }
}
